import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactButton from 'shared/design-system/components/TrueImpactButton';
import TrueImpactTextfield from 'shared/design-system/components/TrueImpactTextfield';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TablePagination from '@mui/material/TablePagination';
import { MenuItem, Select } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faListCheck,
} from '@fortawesome/pro-light-svg-icons';
import {
  Card,
  ErrorBanner,
  EmptyState,
  Pill,
  ScoreBar,
  RatingStars,
  formatDate,
} from './shared';
import FeedbackForm from './FeedbackForm';
import { MatchQuery, getMatches } from './api';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

type FeedbackFilter = 'all' | 'with' | 'without';

const Toolbar = styled.div`
  display: flex;
  gap: ${SPACERS.small};
  align-items: center;
  margin: ${SPACERS.small} 0 ${SPACERS.medium};
  flex-wrap: wrap;
  input {
    padding: 9px 12px;
    font-size: 14px;
    height: 40px;
    box-sizing: border-box;
  }
  .search {
    flex: 1;
    min-width: 220px;
  }
`;

const MatchCard = styled.div`
  border: 1px solid ${COLORS.greyLight};
  border-radius: 8px;
  margin-bottom: ${SPACERS.small};
  overflow: hidden;
  .row-header {
    display: flex;
    align-items: center;
    gap: ${SPACERS.small};
    padding: ${SPACERS.small} ${SPACERS.medium};
    cursor: pointer;
    &:hover {
      background: ${COLORS.greyLightest};
    }
    .query {
      flex: 1;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .query.expanded {
      white-space: normal;
      overflow: visible;
      text-overflow: unset;
      word-break: break-word;
    }
    .meta {
      flex-shrink: 0;
      color: ${COLORS.grayDark};
      font-size: 12px;
      white-space: nowrap;
    }
  }
  .row-detail {
    border-top: 1px solid ${COLORS.greyLight};
    padding: ${SPACERS.medium};
    background: ${COLORS.greyLightest};
  }
`;

const CallerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${SPACERS.tiny} ${SPACERS.medium};
  margin-top: ${SPACERS.tiny};
  .k {
    color: ${COLORS.grayDark};
    font-size: 12px;
  }
  .v {
    font-size: 13px;
    font-family: monospace;
    word-break: break-all;
  }
`;

const DetailSection = styled.div`
  margin-top: ${SPACERS.medium};
  &:first-child {
    margin-top: 0;
  }
`;

const MatchLine = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACERS.small};
  padding: 4px 0;
  .rank {
    width: 22px;
    color: ${COLORS.grayDark};
    font-size: 12px;
    font-weight: 700;
  }
  .name {
    flex: 1;
    min-width: 0;
  }
`;

const FeedbackLine = styled.div`
  padding: ${SPACERS.tiny} 0;
  border-bottom: 1px solid ${COLORS.greyLight};
  &:last-child {
    border-bottom: none;
  }
`;

const CallerInfoBlock: React.FC<{ row: MatchQuery }> = ({ row }) => (
  <CallerGrid>
    {([
      ['IP', row.caller_ip],
      ['Forwarded For', row.forwarded_for],
      ['User Agent', row.user_agent],
      ['Origin', row.origin],
      ['Referer', row.referer],
    ] as [string, string | null][]).map(([k, v]) => (
      <div key={k}>
        <div className="k">{k}</div>
        <div className="v">{v || '—'}</div>
      </div>
    ))}
  </CallerGrid>
);

const ResultsTab: React.FC = () => {
  const [rows, setRows] = useState<MatchQuery[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [feedbackFilter, setFeedbackFilter] = useState<FeedbackFilter>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getMatches({
        limit: perPage,
        offset: page * perPage,
        q: appliedSearch.trim() || undefined,
        only_with_feedback: feedbackFilter === 'with' || undefined,
        only_without_feedback: feedbackFilter === 'without' || undefined,
      });
      setRows(res.items);
      setTotal(res.total);
    } catch (err) {
      setError(
        (err instanceof Error && err.message) ||
          'Failed to load match results.',
      );
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, appliedSearch, feedbackFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <Card>
      <TrueImpactText variant="heading-subheading">
        Match Results
      </TrueImpactText>
      <TrueImpactText
        variant="body-small"
        color="grayDark"
        style={{ display: 'block', marginTop: 4 }}
      >
        Every match request, newest first, with returned templates, caller info,
        and any feedback.
      </TrueImpactText>

      <Toolbar>
        <div className="search">
          <TrueImpactTextfield
            placeholder="Search query text…"
            value={search}
            onChange={v => setSearch(v)}
            onEnterPress={() => {
              setPage(0);
              setAppliedSearch(search);
            }}
          />
        </div>
        <Select
          size="small"
          value={feedbackFilter}
          onChange={e => {
            setPage(0);
            setFeedbackFilter(e.target.value as FeedbackFilter);
          }}
          style={{ minWidth: 170, fontSize: 14, height: 40 }}
        >
          <MenuItem value="all">All matches</MenuItem>
          <MenuItem value="with">With feedback</MenuItem>
          <MenuItem value="without">Without feedback</MenuItem>
        </Select>
        <TrueImpactButton
          variant="secondary-small"
          onClick={() => {
            setPage(0);
            setAppliedSearch(search);
          }}
          style={{ height: 40 }}
        >
          Search
        </TrueImpactButton>
      </Toolbar>

      {error && <ErrorBanner>{error}</ErrorBanner>}

      {loading && rows.length === 0 ? (
        <TrueImpactLoading />
      ) : rows.length === 0 ? (
        <EmptyState>
          <div className="icon">
            <FontAwesomeIcon icon={faListCheck} />
          </div>
          <TrueImpactText>No match requests found.</TrueImpactText>
        </EmptyState>
      ) : (
        <div style={{ opacity: loading ? 0.6 : 1, transition: '0.2s' }}>
          {rows.map(row => {
            const expanded = expandedId === row.id;
            return (
              <MatchCard key={row.id}>
                <div
                  className="row-header"
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                >
                  <TrueImpactText
                    variant="body-small-bold"
                    className={`query${expanded ? ' expanded' : ''}`}
                  >
                    {row.query_text}
                  </TrueImpactText>
                  {row.feedback.length > 0 ? (
                    <Pill tone="success">{row.feedback.length} feedback</Pill>
                  ) : (
                    <Pill>no feedback</Pill>
                  )}
                  <span className="meta">{formatDate(row.created_at)}</span>
                  <FontAwesomeIcon
                    icon={expanded ? faChevronUp : faChevronDown}
                    style={{ color: COLORS.grayDark, fontSize: 12 }}
                  />
                </div>
                {expanded && (
                  <div className="row-detail">
                    <DetailSection>
                      <TrueImpactText variant="body-small-bold">
                        Returned Templates
                      </TrueImpactText>
                      {row.matches.map(m => (
                        <MatchLine key={`${m.template_id}-${m.rank}`}>
                          <span className="rank">#{m.rank}</span>
                          <TrueImpactText variant="body-small" className="name">
                            {m.name ?? (
                              <em style={{ color: COLORS.grayDark }}>
                                deleted template
                              </em>
                            )}
                          </TrueImpactText>
                          <ScoreBar score={m.score} />
                        </MatchLine>
                      ))}
                    </DetailSection>

                    <DetailSection>
                      <TrueImpactText variant="body-small-bold">
                        Feedback
                      </TrueImpactText>
                      {row.feedback.length === 0 ? (
                        <div style={{ marginTop: SPACERS.tiny }}>
                          <FeedbackForm
                            key={row.id}
                            matchId={row.id}
                            matches={row.matches}
                            title="No feedback yet — add one:"
                            onSubmitted={refresh}
                          />
                        </div>
                      ) : (
                        row.feedback.map(f => (
                          <FeedbackLine key={f.id}>
                            <RatingStars value={f.rating} />
                            <TrueImpactText
                              variant="body-small"
                              color="grayDark"
                              style={{ marginLeft: SPACERS.small }}
                            >
                              {formatDate(f.created_at)}
                            </TrueImpactText>
                            {f.comment && (
                              <TrueImpactText
                                variant="body-small"
                                style={{ display: 'block', marginTop: 2 }}
                              >
                                {f.comment}
                              </TrueImpactText>
                            )}
                          </FeedbackLine>
                        ))
                      )}
                    </DetailSection>

                    <DetailSection>
                      <TrueImpactText variant="body-small-bold">
                        Caller Info
                      </TrueImpactText>
                      <CallerInfoBlock row={row} />
                    </DetailSection>

                    <DetailSection>
                      <TrueImpactText variant="body-small" color="grayDark">
                        Match ID:{' '}
                        <span style={{ fontFamily: 'monospace' }}>
                          {row.id}
                        </span>
                      </TrueImpactText>
                    </DetailSection>
                  </div>
                )}
              </MatchCard>
            );
          })}
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={perPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            onRowsPerPageChange={e => {
              setPage(0);
              setPerPage(parseInt(e.target.value, 10));
            }}
          />
        </div>
      )}
    </Card>
  );
};

export default ResultsTab;
